import { Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { EngagementLineItem, EngagementLineUnit } from '@/types'

const UNIT_OPTIONS: { value: EngagementLineUnit; label: string }[] = [
  { value: 'flat',     label: 'flat'     },
  { value: 'hour',     label: 'hr'       },
  { value: 'minute',   label: 'min'      },
  { value: 'headshot', label: 'headshot' },
  { value: 'each',     label: 'each'     },
]

const fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

function newId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `li_${Math.random().toString(36).slice(2, 10)}`
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

interface Props {
  lineItems: EngagementLineItem[]
  discountPercent: number
  onChange: (next: { lineItems: EngagementLineItem[]; discountPercent: number }) => void
}

export function LineItemsEditor({ lineItems, discountPercent, onChange }: Props) {
  function updateRow(id: string, patch: Partial<EngagementLineItem>) {
    const next = lineItems.map((item) => {
      if (item.id !== id) return item
      const merged = { ...item, ...patch }
      // Auto-recompute lineTotal unless caller explicitly overrode it
      if ('qty' in patch || 'unitPrice' in patch) {
        merged.lineTotal = round2(merged.qty * merged.unitPrice)
      }
      return merged
    })
    onChange({ lineItems: next, discountPercent })
  }

  function removeRow(id: string) {
    onChange({ lineItems: lineItems.filter((i) => i.id !== id), discountPercent })
  }

  function addRow() {
    const blank: EngagementLineItem = {
      id: newId(),
      description: '',
      qty: 1,
      unit: 'flat',
      unitPrice: 0,
      lineTotal: 0,
    }
    onChange({ lineItems: [...lineItems, blank], discountPercent })
  }

  const subtotal     = lineItems.reduce((s, i) => s + (Number.isFinite(i.lineTotal) ? i.lineTotal : 0), 0)
  const discountAmt  = round2(subtotal * (discountPercent / 100))
  const grandTotal   = round2(subtotal - discountAmt)

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>Line Items</Label>
        <Button type="button" variant="outline" size="sm" onClick={addRow}>
          <Plus className="mr-1 h-4 w-4" />
          Add line
        </Button>
      </div>

      {lineItems.length === 0 ? (
        <p className="text-xs text-muted-foreground rounded-md border border-dashed p-3">
          No line items yet. Add one to build the estimate.
        </p>
      ) : (
        <div className="space-y-2">
          {lineItems.map((item) => (
            <div
              key={item.id}
              className={`rounded-md border p-3 space-y-2 ${item.isAddon ? 'bg-muted/30' : ''}`}
            >
              <div className="flex items-start gap-2">
                <Input
                  className="flex-1"
                  placeholder="Description"
                  value={item.description}
                  onChange={(e) => updateRow(item.id, { description: e.target.value })}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeRow(item.id)}
                  aria-label="Remove line"
                >
                  <Trash2 className="h-4 w-4 text-muted-foreground" />
                </Button>
              </div>

              <div className="grid grid-cols-[1fr_auto_1fr_auto] items-center gap-2 text-sm">
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  value={item.qty}
                  onChange={(e) => updateRow(item.id, { qty: parseFloat(e.target.value) || 0 })}
                  aria-label="Quantity"
                />
                <Select
                  value={item.unit}
                  onValueChange={(v) => updateRow(item.id, { unit: v as EngagementLineUnit })}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNIT_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-1">
                  <span className="text-muted-foreground">×</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={item.unitPrice}
                    onChange={(e) =>
                      updateRow(item.id, { unitPrice: parseFloat(e.target.value) || 0 })
                    }
                    aria-label="Unit price"
                  />
                </div>
                <span className="font-mono text-sm font-medium tabular-nums">
                  {fmt.format(item.lineTotal)}
                </span>
              </div>

              {item.meta && Object.keys(item.meta).length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {[
                    item.meta.date && `📅 ${item.meta.date}${item.meta.time ? ` @ ${item.meta.time}` : ''}`,
                    item.meta.location && `📍 ${item.meta.location}`,
                    item.meta.complexity && `complexity: ${item.meta.complexity}`,
                    item.meta.includeAudioRecording && 'audio recording',
                  ].filter(Boolean).join('  •  ')}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Totals */}
      <div className="rounded-md border bg-muted/30 p-3 space-y-1.5 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Subtotal</span>
          <span className="font-mono tabular-nums">{fmt.format(subtotal)}</span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <Label htmlFor="discount-pct" className="text-muted-foreground font-normal flex items-center gap-1">
            Discount
            <Input
              id="discount-pct"
              type="number"
              min="0"
              max="100"
              step="1"
              className="h-7 w-16 text-right"
              value={discountPercent}
              onChange={(e) =>
                onChange({
                  lineItems,
                  discountPercent: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)),
                })
              }
            />
            %
          </Label>
          <span className="font-mono tabular-nums text-muted-foreground">
            −{fmt.format(discountAmt)}
          </span>
        </div>
        <div className="flex items-center justify-between border-t pt-1.5 font-semibold">
          <span>Estimate Total</span>
          <span className="font-mono tabular-nums">{fmt.format(grandTotal)}</span>
        </div>
      </div>
    </div>
  )
}
